/**
 * IL GESTIONALE È SOLO PER AMMINISTRATORI E CONSULENTI — tutti gli altri vanno sul portale nuovo.
 *
 * Corsisti (CLIENT) e docenti (TEACHER) lavorano sul portale nuovo, ma continuano ad arrivare qui:
 * un segnalibro, un vecchio link, l'abitudine. Li portiamo di la' GIA' DENTRO, senza far riscrivere
 * la password (e senza far ripetere il codice via SMS a chi ha il secondo fattore).
 *
 * COME: si chiede al backend un gettone monouso valido un minuto e si arriva al portale con quello
 * nell'indirizzo; il portale lo scambia con una sessione vera. Il token di sessione NON passa mai per
 * l'indirizzo — finirebbe nella cronologia e nei log (vedi handoff.controller.js).
 *
 * ┌─ 🔴 FINO AL 29/09/2026 IL PRINCIPIO ERA «NEL DUBBIO NON SI FA NIENTE», ED ERA IL GUASTO ──────┐
 * │                                                                                            │
 * │ Se il passaggio non partiva, l'utente restava qui «e lavorava come sempre». E si tentava   │
 * │ UNA VOLTA SOLA per scheda. Risultato, segnalato dai docenti: un docente passato al portale │
 * │ che tornava indietro nella stessa scheda (o a cui il gettone scadeva, e il portale gli      │
 * │ offriva «Torna al gestionale») ritrovava qui la sua sessione ancora valida, il segno «gia'  │
 * │ tentato», e restava dentro il gestionale.                                                  │
 * └────────────────────────────────────────────────────────────────────────────────────────────┘
 *
 * LE REGOLE DI ADESSO:
 *
 *  1. NON SI RESTA. Chi non e' ADMIN o CONSULENT non vede mai il gestionale: o passa al portale gia'
 *     dentro, o arriva alla pagina di accesso del portale. Anche un ruolo che non si riesce a leggere
 *     sta fuori: un amministratore rientra dal login, un docente no.
 *
 *  2. LA SESSIONE DI QUI SI LASCIA PRIMA DI PARTIRE. Il token del gestionale si toglie da questo
 *     browser: tornando indietro si trova il login, non una sessione ancora valida. La sessione sul
 *     server invece resta — e' la stessa che il portale riceve con il gettone.
 *
 *  3. SE IL PASSAGGIO NON PARTE, la sessione si chiude anche sul server (non la userebbe nessuno) e
 *     si va al login del portale. Niente giri in tondo: da qui in poi il gestionale non ha piu' una
 *     sessione da offrire, quindi non c'e' nessun «una volta sola» da ricordare.
 *
 * CHI ENTRA DAVVERO lo decide comunque il backend, rotta per rotta: questo e' il confine della
 * schermata, non quello dei permessi.
 */

// Gli unici che lavorano sul gestionale. Tutti gli altri ruoli vanno sul portale.
const RUOLI_GESTIONALE = ['ADMIN', 'CONSULENT'];

function indirizzoPortale() {
    const u = (process.env.REACT_APP_PORTAL_V2_URL || '').trim();
    return u === '' ? null : u.replace(/\/+$/, '');
}

/**
 * Il ruolo dell'utente, da qualunque forma arrivi.
 * ⚠️ Il gestionale salva `userData` in DUE forme diverse: dopo il login normale e' l'oggetto
 * intero della risposta (quindi il ruolo sta in `.user.role`), dopo il codice via SMS e dopo
 * `/auth/permission` e' gia' l'utente piatto (`.role`). Chi legge solo una delle due sbaglia meta'
 * dei casi.
 */
export function ruoloDi(userData) {
    if (!userData || typeof userData !== 'object') return null;
    if (userData.role) return userData.role;
    if (userData.user && userData.user.role) return userData.user.role;
    return null;
}

/** true se questo utente NON deve stare nel gestionale (regola 1: anche se il ruolo non si legge). */
export function daSpostare(userData) {
    return !RUOLI_GESTIONALE.includes(ruoloDi(userData));
}

/** Toglie la sessione del gestionale da QUESTO browser. Il server non lo tocca. */
function lasciaSessioneLocale() {
    try { localStorage.removeItem('hash'); } catch (e) { /* niente storage: niente da togliere */ }
    try { sessionStorage.removeItem('userData'); } catch (e) { /* idem */ }
    global.userData = undefined;
}

/**
 * Porta fuori dal gestionale chi non ci deve stare. Non lancia MAI.
 *
 * @param {object} userData  l'utente appena entrato, o quello gia' in sessione
 * @returns {Promise<boolean>}  true se la pagina sta per cambiare: chi chiama NON deve mostrare
 *                              il gestionale. false solo per ADMIN e CONSULENT.
 */
export async function mandaAlPortale(userData) {
    if (!daSpostare(userData)) return false;   // amministratori e consulenti restano qui

    const portale = indirizzoPortale();
    let token = null;
    try { token = localStorage.getItem('hash'); } catch (e) { token = null; }

    // Un ruolo che non si legge non e' un docente da mandare di la': e' una risposta che non
    // capiamo. Si esce e si rientra dal login, dove la risposta del server e' quella completa.
    const ruolo = ruoloDi(userData);

    // ── 1. il passaggio gia' dentro ──────────────────────────────────────────────────────────
    if (portale && token && ruolo) {
        try {
            const risposta = await fetch(global.route.authHandoff, {
                method: 'POST',
                headers: { 'x-access-token': token, 'Content-Type': 'application/json' }
            });
            const dati = risposta.ok ? await risposta.json() : null;

            if (dati && dati.status === 'HANDOFF_CREATED' && dati.token) {
                // Regola 2: prima di partire. Il gettone porta con se' la sessione del server,
                // quindi il token di qui non serve piu' a nessuno — e tenerlo e' esattamente il
                // modo in cui un docente restava dentro.
                lasciaSessioneLocale();
                window.location.replace(portale + '/handoff?t=' + encodeURIComponent(dati.token));
                return true;
            }
        } catch (e) {
            console.warn('passaggio al portale non riuscito: si chiude la sessione del gestionale', e);
        }
    }

    // ── 2. il passaggio non e' partito: si esce comunque (regola 3) ──────────────────────────
    // La sessione non la sta usando nessun altro: nessun gettone e' stato emesso su di lei.
    if (token) {
        try {
            await fetch(global.route.logout, {
                method: 'GET',
                headers: { 'x-access-token': token, 'Content-Type': 'application/json' }
            });
        } catch (e) { /* best effort: il token di qui sparisce comunque */ }
    }
    lasciaSessioneLocale();

    if (!ruolo) window.location.replace('/login');
    else if (portale) window.location.replace(portale + '/login');
    else window.location.replace('/unauthorized');
    return true;
}
